/**
 * Sale / attention bar.
 *
 * The sale bar and the utility info bar share the top-of-header slot — only one
 * shows at a time. While the sale bar is active it replaces the utility bar;
 * dismissing it (✕) brings the utility bar back. Dismissal is remembered per
 * section in sessionStorage — i.e. only for the current browser session: it
 * sticks across page navigation within a visit, but a returning visitor sees the
 * bar again, so an active sale keeps reminding them. (Was localStorage, which
 * silenced the bar forever on that device.)
 *
 * Visibility is toggled with the `.hidden` utility class (display:none !important)
 * — NOT the `hidden` attribute — because both bars carry the `.section` class,
 * whose `display: grid` rule (base.css) overrides `[hidden] { display: none }`.
 *
 * We toggle each section's `.shopify-section` WRAPPER (not the inner element) so
 * the utility bar's sibling `.section-background` div hides along with it.
 *
 * In the theme editor the swap still runs (so it looks correct), but selecting
 * either bar in the editor reveals it so the merchant can edit both.
 */
class SaleBarComponent extends HTMLElement {
  connectedCallback() {
    this.storageKey = `sale-bar-dismissed:${this.dataset.sectionId}`;
    this.dismissible = this.dataset.dismissible === 'true';
    this.hideMobile = this.dataset.hideMobile === 'true';
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.dismissed = !this.isDesignMode() && this.dismissible && this.getStored() === '1';

    this.querySelector('[data-sale-bar-close]')?.addEventListener('click', () => this.dismiss());

    // Whole-bar click: when a link is set, navigate from anywhere on the bar — a
    // mouse convenience layered over the real, focusable CTA/message links (kept
    // in the markup for keyboard/screen-reader users). See onBarClick for the
    // exclusions (close ✕, real links, editor).
    this.ctaLink = this.dataset.ctaLink || '';
    if (this.ctaLink) {
      // `auxclick` too: middle-click fires auxclick, not click (for new-tab parity).
      this.addEventListener('click', (event) => this.onBarClick(event));
      this.addEventListener('auxclick', (event) => this.onBarClick(event));
    }

    this.render();

    // Re-run the swap when the viewport crosses the mobile breakpoint, so the
    // hide-on-mobile bar appears/disappears (and the utility bar takes its slot)
    // on resize/orientation change without a reload.
    if (this.hideMobile) {
      this.mobileQuery.addEventListener('change', () => this.render());
    }

    if (this.isDesignMode()) this.watchEditor();
  }

  /** Suppressed when dismissed, or when hide-on-mobile is on at a small viewport. */
  shouldHide() {
    return this.dismissed || (this.hideMobile && this.mobileQuery.matches);
  }

  isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  getStored() {
    try {
      return sessionStorage.getItem(this.storageKey);
    } catch (error) {
      return null;
    }
  }

  /** This component's own Shopify section wrapper (holds its .section-background too). */
  saleSection() {
    return this.closest('.shopify-section');
  }

  /** The utility bar's section wrapper, or null if that section isn't present. */
  utilitySection() {
    return document.querySelector('.utility-bar')?.closest('.shopify-section') ?? null;
  }

  /** Hide/show via the `.hidden` class (beats `.section { display: grid }`). */
  setHidden(element, hidden) {
    if (!element) return;
    element.classList.toggle('hidden', hidden);
  }

  /**
   * Show exactly one bar: the sale bar when active (not dismissed and not
   * hidden-on-mobile), otherwise the utility bar.
   * Toggles each section's `.shopify-section` wrapper so the `.section-background`
   * element (which paints the color scheme) hides along with its bar.
   */
  render() {
    const showSale = !this.shouldHide();
    this.setHidden(this.saleSection(), !showSale);
    this.setHidden(this.utilitySection(), showSale);
  }

  /**
   * Navigate to the bar's link when the user clicks anywhere that isn't already
   * interactive. We bail on the close ✕ and any real <a>/<button> so they keep
   * their native behavior (and we don't double-fire on the CTA link). In the
   * editor we never navigate, so clicking the bar selects the section instead.
   */
  onBarClick(event) {
    if (this.isDesignMode()) return;
    if (event.button === 2) return; // right-click → leave the context menu alone
    if (event.target.closest('a, button, [data-sale-bar-close]')) return;

    // Mirror normal link behavior: modifier / middle click opens a new tab.
    if (event.metaKey || event.ctrlKey || event.button === 1) {
      window.open(this.ctaLink, '_blank');
    } else {
      window.location.href = this.ctaLink;
    }
  }

  dismiss() {
    // In the editor, dismissing is a preview only — don't persist it.
    // sessionStorage (not localStorage): the dismissal lasts only for this
    // browser session, so a returning visitor is reminded of the sale again.
    if (!this.isDesignMode()) {
      try {
        sessionStorage.setItem(this.storageKey, '1');
      } catch (error) {
        // sessionStorage may be unavailable (private mode); ignore.
      }
    }
    this.dismissed = true;
    this.render();
  }

  /** Keep both bars reachable while editing: reveal whichever one is selected. */
  watchEditor() {
    document.addEventListener('shopify:section:select', (event) => {
      const sale = this.saleSection();
      const utility = this.utilitySection();

      if (utility && event.target === utility) {
        this.setHidden(utility, false);
        this.setHidden(sale, true);
      } else if (sale && event.target === sale) {
        this.setHidden(sale, false);
        this.setHidden(utility, true);
      }
    });

    document.addEventListener('shopify:section:deselect', () => this.render());
  }
}

if (!customElements.get('sale-bar-component')) {
  customElements.define('sale-bar-component', SaleBarComponent);
}
